import { motion } from 'framer-motion';
import { CheckCircle, Target, Eye, Heart, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const values = [
  { icon: Heart, title: 'Integrity', desc: 'Unwavering honesty and ethical standards across every operation and partnership.' },
  { icon: Target, title: 'Professionalism', desc: 'Delivering engineering excellence from manufacturing to final delivery.' },
  { icon: CheckCircle, title: 'Teamwork', desc: 'Over 100 professionals collaborating toward customer satisfaction.' },
  { icon: Eye, title: 'Customer First', desc: 'Every decision is driven by the success and trust of our customers.' },
];

const milestones = [
  { year: '2009', event: 'Founded in Coimbatore, Tamil Nadu with a vision to serve industrial India' },
  { year: '2012', event: 'Established first air separation plant LOX / LIN / LAR production begins' },
  { year: '2015', event: 'Medical gas manufacturing division launched; hospital supply program started' },
  { year: '2018', event: 'Opened Trichy branch; scaled to 5 plants across South India' },
  { year: '2020', event: 'Covid Pandemic: 12 seated oxygen bus and 10+ medical hospitals O2 supply with 24/7 support' },
  { year: '2021', event: 'Reached 9 plants nationwide with full international export capability' },
  { year: '2024', event: 'Setting up 400-bar high-pressure cylinder filling station next-gen infrastructure' },
  { year: '2026', event: 'Global crisis: supplying alternative industrial gases for LPG shortage' },
];

export default function AboutPage() {
  return (
    <div className="pt-24">

      {/* ── HERO ── */}
      <section className="py-24 bg-[#181818] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1558382689-c1c29cc9b37e?auto=format&fit=crop&w=2000&q=60)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-[#181818]/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-[#CC2222]" />
              <span className="text-[#CC2222] text-[11px] font-bold uppercase tracking-[0.3em]">About Seva Gases</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter font-heading text-white leading-none mb-6">
              Our Story.<br /><span className="text-white/40">Our Purpose.</span>
            </h1>
            <p className="text-white/50 text-lg leading-relaxed max-w-xl">
              From a single plant in Coimbatore to 9 projects across India 15 years of precision, trust, and all-in-one gas solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── OVERVIEW ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div className="relative">
                <img src="https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/9s1qnnl5_image.png" alt="Seva Gases industrial facility" className="w-full h-[520px] object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-4 block">Who We Are</span>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-heading text-[#111111] mb-6 leading-tight">
                Your Trusted All-in-One Gas Partner
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Seva Gases is one of the leading energy companies in India involved in the manufacturing, trading, importing, and exporting of industrial and medical gases to corporations and institutions across the globe.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                We manufacture and supply industrial, medical, mixture, specialities, instrumentation and laboratory gases. We also undertake erection of storage tanks, pipelines, and complete turn-key project solutions.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                With liquid plant capacities from 50–500 TPD and gas plant capacities up to 1000 TPD, we are India's most capable all-in-one provider from planning and design through to long-term support.
              </p>
              <div className="grid grid-cols-3 gap-6 border-t border-gray-100 pt-8 mb-8">
                {[{ v: '9', l: 'Plants' }, { v: '100+', l: 'Staff' }, { v: '15+', l: 'Years' }].map(s => (
                  <div key={s.l}>
                    <div className="text-3xl font-black text-[#CC2222] font-heading">{s.v}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-bold">{s.l}</div>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#CC2222] text-white px-7 py-3.5 font-bold text-sm uppercase tracking-wider hover:bg-[#A81C1C] transition-colors group">
                Get in Touch <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── VISION & MISSION ── */}
      <section className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">Direction</span>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-heading text-[#111111]">Vision & Mission</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            <Reveal className="bg-white border border-gray-200 p-10 border-l-4 border-l-[#CC2222]">
              <div className="w-10 h-10 bg-[#CC2222]/10 flex items-center justify-center mb-6">
                <Eye size={20} className="text-[#CC2222]" />
              </div>
              <h3 className="text-2xl font-black font-heading text-[#111111] mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To revolutionize the gas industry by acting as a catalyst for progress becoming the best in the industry, recognized globally for quality, innovation, and reliability.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="bg-[#181818] p-10 border-l-4 border-l-[#CC2222]">
              <div className="w-10 h-10 bg-white/10 flex items-center justify-center mb-6">
                <Target size={20} className="text-[#CC2222]" />
              </div>
              <h3 className="text-2xl font-black font-heading text-white mb-4">Our Mission</h3>
              <ul className="space-y-3">
                {[
                  'Provide comprehensive gas solutions responsive to dynamic market needs',
                  'Enhance participation in commercial, industrial, and medical sectors in India and internationally',
                  'Create opportunities for our people to excel in their chosen field of expertise',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-[#CC2222] rounded-full mt-2 flex-shrink-0" />
                    <span className="text-white/50 text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── CORE VALUES ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14">
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">What Drives Us</span>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-heading text-[#111111]">Core Values</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="border border-gray-200 p-8 group hover:border-[#CC2222] hover:shadow-lg transition-all duration-300 bg-white" data-testid={`value-${v.title.toLowerCase()}`}>
                <div className="w-11 h-11 bg-[#F5F5F5] group-hover:bg-[#CC2222] flex items-center justify-center mb-5 transition-colors duration-300">
                  <v.icon size={20} className="text-[#CC2222] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-black text-[#111111] font-heading text-lg mb-3">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUALITY POLICY ── */}
      <section className="py-24 bg-[#CC2222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/50 mb-4 block">Our Commitment</span>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-heading text-white mb-6">Quality Policy</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                Seva Gases ensures world class standards and procedures while developing its product range. The company has deployed complete quality control instruments across all stages of manufacture.
              </p>
              <p className="text-white/70 leading-relaxed">
                Products are analyzed for purity during every step of dispatch. Quality and safety are the highest priorities we continuously upgrade facilities and provide best-in-class training to achieve maximum customer satisfaction.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-1">
                {['World-class Standards', 'Complete QC at All Stages', 'Purity Analysis per Batch', 'Certified Processes', 'Safety-First Culture', 'Continuous Improvement'].map(item => (
                  <div key={item} className="bg-white/10 p-5 border border-white/20">
                    <CheckCircle size={16} className="text-white/50 mb-2" />
                    <p className="text-white text-sm font-semibold leading-snug">{item}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── TIMELINE — SVG Winding Road ── */}
      <section className="py-24 bg-white overflow-hidden" data-testid="timeline-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">Our Journey</span>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-heading text-[#111111]">Key Milestones</h2>
            <p className="text-gray-400 text-sm mt-4 max-w-xs mx-auto">15 years of growth, from one plant to 9 projects across India.</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="w-full overflow-x-auto -mx-4 px-4">
              <svg
                viewBox="0 0 1000 590"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full"
                style={{ minWidth: '480px', maxWidth: '900px', display: 'block', margin: '0 auto' }}
              >
                {/* ── Road: shadow ── */}
                <path
                  d="M -10,110 H 770 A 85,85 0 0 1 770,280 H 230 A 85,85 0 0 0 230,450 H 1010"
                  stroke="rgba(0,0,0,0.18)" strokeWidth="84" fill="none" strokeLinecap="round"
                  transform="translate(0,5)"
                />
                {/* ── Road: surface ── */}
                <path
                  d="M -10,110 H 770 A 85,85 0 0 1 770,280 H 230 A 85,85 0 0 0 230,450 H 1010"
                  stroke="#363636" strokeWidth="80" fill="none" strokeLinecap="round"
                />
                {/* ── Road: edge highlights ── */}
                <path
                  d="M -10,110 H 770 A 85,85 0 0 1 770,280 H 230 A 85,85 0 0 0 230,450 H 1010"
                  stroke="rgba(255,255,255,0.05)" strokeWidth="80" fill="none" strokeLinecap="round"
                />
                {/* ── Road: center dashes ── */}
                <path
                  d="M -10,110 H 770 A 85,85 0 0 1 770,280 H 230 A 85,85 0 0 0 230,450 H 1010"
                  stroke="rgba(255,255,255,0.55)" strokeWidth="3" fill="none"
                  strokeDasharray="22 14" strokeLinecap="butt"
                />

                {/* ══ MILESTONE 1 — 2009 · Founded ══ */}
                <line x1="150" y1="68" x2="150" y2="42" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="150" cy="110" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="150" cy="110" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="150" y="106" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2009</text>
                <text x="150" y="120" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">FOUNDED</text>
                <text x="150" y="33" textAnchor="middle" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">Established</text>
                <text x="150" y="17" textAnchor="middle" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">Coimbatore, Tamil Nadu</text>

                {/* ══ MILESTONE 2 — 2012 · First Plant ══ */}
                <line x1="510" y1="68" x2="510" y2="42" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="510" cy="110" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="510" cy="110" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="510" y="106" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2012</text>
                <text x="510" y="120" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">1ST PLANT</text>
                <text x="510" y="33" textAnchor="middle" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">Air Separation Plant</text>
                <text x="510" y="17" textAnchor="middle" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">LOX · LIN · LAR production</text>

                {/* ══ MILESTONE 3 — 2015 · Medical ══ (right U-turn peak — label right-aligned to viewBox edge) */}
                <line x1="897" y1="195" x2="968" y2="158" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="855" cy="195" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="855" cy="195" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="855" y="191" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2015</text>
                <text x="855" y="205" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">MEDICAL</text>
                <text x="996" y="151" textAnchor="end" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">Medical Division</text>
                <text x="996" y="136" textAnchor="end" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">Hospital supply</text>

                {/* ══ MILESTONE 4 — 2018 · Expansion ══ */}
                <line x1="460" y1="322" x2="460" y2="352" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="460" cy="280" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="460" cy="280" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="460" y="276" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2018</text>
                <text x="460" y="290" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">EXPANSION</text>
                <text x="460" y="364" textAnchor="middle" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">South India Growth</text>
                <text x="460" y="378" textAnchor="middle" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">Trichy branch — 5 plants</text>

                {/* ══ MILESTONE 5 — 2020 · Covid ══ (Row 2, between 2018 and left U-turn — label above road) */}
                <line x1="320" y1="238" x2="320" y2="218" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="320" cy="280" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="320" cy="280" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="320" y="276" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2020</text>
                <text x="320" y="290" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">COVID</text>
                <text x="320" y="210" textAnchor="middle" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">Covid Pandemic</text>
                <text x="320" y="194" textAnchor="middle" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">O2 buses &amp; hospital support</text>

                {/* ══ MILESTONE 6 — 2021 · National ══ (left U-turn peak — label left-aligned to viewBox edge) */}
                <line x1="103" y1="407" x2="18" y2="490" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="145" cy="365" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="145" cy="365" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="145" y="361" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2021</text>
                <text x="145" y="375" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">NATIONAL</text>
                <text x="8" y="497" textAnchor="start" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">National Reach</text>
                <text x="8" y="511" textAnchor="start" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">9 plants · global export</text>

                {/* ══ MILESTONE 7 — 2024 · Next Gen ══ */}
                <line x1="660" y1="492" x2="660" y2="518" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="660" cy="450" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="660" cy="450" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="660" y="446" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2024</text>
                <text x="660" y="460" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">NEXT GEN</text>
                <text x="660" y="530" textAnchor="middle" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">Next-Gen Infrastructure</text>
                <text x="660" y="545" textAnchor="middle" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">400-bar high-pressure station</text>

                {/* ══ MILESTONE 8 — 2026 · Global Crisis ══ */}
                <line x1="870" y1="492" x2="870" y2="518" stroke="#CC2222" strokeWidth="1.5" opacity="0.75"/>
                <circle cx="870" cy="450" r="42" fill="rgba(204,34,34,0.12)"/>
                <circle cx="870" cy="450" r="34" fill="#CC2222" stroke="white" strokeWidth="3"/>
                <text x="870" y="446" textAnchor="middle" fill="white" fontWeight="900" fontSize="11.5" fontFamily="system-ui,sans-serif">2026</text>
                <text x="870" y="460" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontWeight="700" fontSize="7.5" fontFamily="system-ui,sans-serif">GLOBAL</text>
                <text x="870" y="530" textAnchor="middle" fill="#111111" fontWeight="800" fontSize="12.5" fontFamily="system-ui,sans-serif">Global Crisis Response</text>
                <text x="870" y="545" textAnchor="middle" fill="#999999" fontSize="10" fontFamily="system-ui,sans-serif">Alt. industrial gases for LPG</text>
              </svg>
            </div>
          </Reveal>
        </div>
      </section>
      {/* ── CTA ── */}
      <section className="py-20 bg-[#F5F5F5] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl font-black text-[#111111] font-heading mb-3">Partner with Seva Gases</h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto text-sm">Join 100+ corporations across India who trust Seva Gases for end-to-end gas solutions.</p>
            <Link to="/contact" data-testid="about-cta-btn" className="inline-flex items-center gap-2 bg-[#CC2222] text-white px-8 py-4 font-bold text-sm uppercase tracking-wider hover:bg-[#A81C1C] transition-colors group">
              Get in Touch <ArrowUpRight size={14} className="group-hover:rotate-12 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
